import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { updateUser, getAllUsers } from '../../../../store/user.js';

export const useClientData = () => {
	const { username } = useParams();
	const dispatch = useDispatch();
	const token = useSelector((st) => st.admin.token); // Get token from Redux
	const users = useSelector((st) => st.user.allUsers); // Get users from Redux
	const pending = useSelector((st) => st.user.pending); // Get loading state from Redux
	const [client, setClient] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [isEditMode, setIsEditMode] = useState({});

	// Fetch all users and find the specific client (same approach as legacy)
	const fetchClient = useCallback(async () => {
		if (!username || !token) return;

		setLoading(true);
		setError(null);

		try {
			// Use Redux action to fetch users (same as legacy)
			await dispatch(getAllUsers({ token, status: 'all' }));
		} catch (err) {
			console.error('Error fetching client:', err);
			setError(err.message);
		} finally {
			setLoading(false);
		}
	}, [username, token, dispatch]);

	// Update a specific field using Redux action (same as legacy)
	const updateField = useCallback(
		async (fieldName, value) => {
			if (!client) return false;

			try {
				// Use Redux updateUser action (same as legacy)
				await dispatch(
					updateUser({
						username,
						data: { [fieldName]: value },
						token,
					})
				).unwrap();

				// Refresh user data (same as legacy)
				dispatch(getAllUsers({ token, status: 'all' }));

				// Exit edit mode for this field
				setIsEditMode((prev) => ({
					...prev,
					[fieldName]: false,
				}));

				return true;
			} catch (err) {
				console.error(`Error updating ${fieldName}:`, err);
				setError(err.message);
				return false;
			}
		},
		[client, username, token, dispatch]
	);

	// Toggle edit mode for a specific field
	const toggleEditMode = useCallback((fieldName) => {
		setIsEditMode((prev) => ({
			...prev,
			[fieldName]: !prev[fieldName],
		}));
	}, []);

	// Cancel edit mode for a specific field
	const cancelEdit = useCallback((fieldName) => {
		setIsEditMode((prev) => ({
			...prev,
			[fieldName]: false,
		}));
	}, []);

	// Update client when users change (same as legacy)
	useEffect(() => {
		const currentUser = users.find((u) => u.username === username);
		if (currentUser) {
			setClient(currentUser);
		}
	}, [users, username]);

	// Load users on mount
	useEffect(() => {
		fetchClient();
	}, [fetchClient]);

	return {
		client,
		setClient,
		loading,
		error,
		isEditMode,
		token,
		fetchClient,
		updateField,
		toggleEditMode,
		cancelEdit,
	};
};
